import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dna, Cpu, Plus, Package, Inbox, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OrderStatusChip } from "@/components/marketplace/OrderStatusChip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const NEXT = { recommended: "ordered", ordered: "sample_collected", sample_collected: "result_ready" };
const NEXT_LABEL = { recommended: "Accept order", ordered: "Sample collected", sample_collected: "Result ready" };
const EMPTY = { name: "", category: "", price: "", sample_type: "", turnaround: "", description: "", tags: "" };

function ProductDialog({ brandId, open, onClose }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await api.post(`/marketplace/brands/${brandId}/products`, { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Product added"); onClose(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add product</DialogTitle><DialogDescription>Add a test or device to your marketplace catalogue.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="product-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="e.g. Preventive panel" /></div>
            <div><Label>Price</Label><Input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="₹1,999" /></div>
            <div><Label>Sample type</Label><Input value={f.sample_type} onChange={(e) => setF({ ...f, sample_type: e.target.value })} /></div>
            <div><Label>Turnaround</Label><Input value={f.turnaround} onChange={(e) => setF({ ...f, turnaround: e.target.value })} /></div>
          </div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div><Label>Tags (comma separated)</Label><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="HbA1c, Lipid" /></div>
          <div className="flex justify-end gap-2 pt-1"><Button variant="outline" onClick={() => onClose()}>Cancel</Button><Button className="bg-teal-700 hover:bg-teal-800 rounded-full" disabled={busy} onClick={save} data-testid="save-product-button">Add product</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BrandDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const load = () => api.get("/marketplace/my-brand").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const brand = data.brand;
  const Icon = user.role === "dx" ? Dna : Cpu;

  if (!brand) return (
    <div className="clinical-card p-8 text-center text-sm text-slate-500" data-testid="no-brand-notice">
      No brand is linked to {user.email} yet. Ask a CareStop admin to create your brand profile in the marketplace.
    </div>
  );

  const advance = async (o) => { await api.put(`/marketplace/orders/${o.id}/status`, { status: NEXT[o.status] }); load(); };

  return (
    <div className="space-y-8" data-testid="brand-dashboard">
      <div className="flex flex-wrap items-center gap-4">
        <span className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-800 grid place-items-center font-heading font-extrabold text-2xl">{brand.name?.[0]}</span>
        <div className="flex-1">
          <p className="eyebrow flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {user.role === "dx" ? "Diagnostics brand" : "Medtech brand"}</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{brand.name}</h1>
          <p className="text-sm text-slate-600 mt-1">{brand.tagline}</p>
        </div>
        <Link to={`/marketplace/${brand.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:underline" data-testid="view-public-brand"><ExternalLink className="w-4 h-4" /> View public page</Link>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2"><Package className="w-5 h-5 text-teal-700" /> Your catalogue ({brand.products?.length || 0})</h2>
          <Button className="bg-teal-700 hover:bg-teal-800 rounded-full" onClick={() => setShowAdd(true)} data-testid="add-product-button"><Plus className="w-4 h-4 mr-1" /> Add product</Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {brand.products?.map((p) => (
            <div key={p.id} className="clinical-card p-5" data-testid={`my-product-${p.id}`}>
              <div className="flex items-start justify-between gap-3"><p className="font-heading font-semibold text-slate-900">{p.name}</p>{p.price && <span className="text-sm font-bold text-teal-800">{p.price}</span>}</div>
              {p.description && <p className="text-sm text-slate-600 mt-1.5">{p.description}</p>}
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2"><Inbox className="w-5 h-5 text-teal-700" /> Incoming assignments ({data.orders?.length || 0})</h2>
        {data.orders?.length === 0 && <p className="text-sm text-slate-500">No assignments yet. Clinicians will appear here when they recommend your products.</p>}
        <div className="space-y-3">
          {data.orders?.map((o) => (
            <div key={o.id} className="clinical-card p-5 flex flex-wrap items-center gap-4" data-testid={`incoming-order-${o.id}`}>
              <div className="flex-1 min-w-[220px]">
                <p className="font-heading font-semibold text-slate-900">{o.product_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">For {o.patient_name} · assigned by {o.assigned_by_name} · {fmtDateTime(o.created_at)}</p>
                {o.note && <p className="text-sm text-slate-600 mt-1.5">“{o.note}”</p>}
              </div>
              <OrderStatusChip status={o.status} />
              {NEXT[o.status] && <button data-testid={`brand-advance-${o.id}`} onClick={() => advance(o)} className="text-sm font-semibold text-teal-700 hover:underline whitespace-nowrap">{NEXT_LABEL[o.status]} →</button>}
            </div>
          ))}
        </div>
      </section>
      {showAdd && <ProductDialog brandId={brand.id} open={showAdd} onClose={(ok) => { setShowAdd(false); if (ok) load(); }} />}
    </div>
  );
}
